import { PermissionKey, PermissionScope } from "@fitness-app/data-models";

import { type PermissionRequirement } from "~/modules/Auth/permissionRequirement";

export const dashboardPermissionRequirements = {
  ai: {
    allOf: [{ permission: PermissionKey.AGENTS_USE }, { permission: PermissionKey.CHAT_READ }],
  },
  appConfig: { permission: PermissionKey.APP_CONFIG_MANAGE, minimumScope: PermissionScope.ALL },
  automation: { permission: PermissionKey.AUTOMATION_MANAGE, minimumScope: PermissionScope.ALL },
  /**
   * Zakładka „Kalendarz" (TRE-294) - konsultacje, które prowadzi trener.
   *
   * `clients.write`, a nie `clients.read`, bo to narzędzie zespołu prowadzącego
   * klientki: sprzedaż i support mają `clients.read` ALL, ale konsultacji nie
   * prowadzą. Czyje konsultacje widać, rozstrzyga w widoku `clients.read` ALL
   * (`useCalendarTrainer`), lustro polityki odczytu na `calendar_event`.
   */
  calendar: { permission: PermissionKey.CLIENTS_WRITE },
  campaigns: { permission: PermissionKey.CAMPAIGNS_MANAGE },
  chat: { permission: PermissionKey.CHAT_READ },
  // Zakres lustrzany do chat.read, bo to chat.read filtruje wiersze kolejki: head
  // coach prowadzi ją na TEAM (swoje i swoich trenerów), administracja i właściciel
  // na ALL. Nadanie klucza nie poszerza więc nikomu widoczności klientek.
  chatAlerts: { permission: PermissionKey.CHAT_ALERTS_READ, minimumScope: PermissionScope.TEAM },
  chatMetrics: { permission: PermissionKey.TEAM_CHAT_METRICS_READ, minimumScope: PermissionScope.ALL },
  engagement: {
    anyOf: [{ permission: PermissionKey.ENGAGEMENT_MANAGE }, { permission: PermissionKey.ENGAGEMENT_BADGES_MANAGE }],
  },
  /**
   * Sekcja „Centrum pomocy" (`/help-center`) — instrukcje i wpisy „Co nowego"
   * dla klientek.
   *
   * `anyOf`, bo publikacja jest osobnym kluczem: ktoś z samym
   * `helpCenter.publish` ma wejść na listę i wypuścić gotowy materiał, a bez
   * tego ramienia odbijałby się na `/dashboard` mimo klucza. Lustro polityk na
   * `help_article` — czytają oba klucze, a publikuje wyłącznie `publish`.
   * Bez flagi PostHog: `client-help-center` bramkuje wejścia w aplikacji
   * klientki, nie panel.
   */
  helpCenter: {
    anyOf: [
      { permission: PermissionKey.HELP_CENTER_MANAGE, minimumScope: PermissionScope.ALL },
      { permission: PermissionKey.HELP_CENTER_PUBLISH, minimumScope: PermissionScope.ALL },
    ],
  },
  landings: { permission: PermissionKey.LANDINGS_MANAGE, minimumScope: PermissionScope.ALL },
  marketing: { permission: PermissionKey.MARKETING_READ, minimumScope: PermissionScope.ALL },
  nutrition: { permission: PermissionKey.NUTRITION_MANAGE },
  products: {
    anyOf: [
      { permission: PermissionKey.PRODUCTS_MANAGE, minimumScope: PermissionScope.ALL },
      { permission: PermissionKey.PRODUCTS_RESOURCES_READ },
      { permission: PermissionKey.PRODUCTS_RESOURCES_WRITE },
    ],
  },
  productsManage: { permission: PermissionKey.PRODUCTS_MANAGE, minimumScope: PermissionScope.ALL },
  /**
   * Zakładka „Klienci" w Produktach i karta klienta.
   *
   * `clients.read` ALL, bo zapytanie o listę (`useProductsCustomers`) nie dopina
   * żadnego filtra po przypisanym trenerze, a polityki na `customer` /
   * `product_order` pytają tylko o OBECNOŚĆ klucza, nie o zakres — na ASSIGNED
   * zwykła trenerka zobaczyłaby cały tenant razem z przychodami.
   *
   * `clients.write` ALL dokłada rozróżnienie, którego sam odczyt nie daje:
   * SALES_REP ma `clients.read` ALL (dane klientek są powiązane z jego
   * spotkaniami), ale nie ma `clients.write` w ogóle — czyta, nie administruje,
   * więc kartoteki klientów produktowych nie dostaje. Administracja ma oba ALL,
   * także te konta bez `products.manage`. Operacje na zamówieniach w środku mają
   * własną bramkę `products.manage`.
   */
  productsCustomers: {
    allOf: [
      { permission: PermissionKey.CLIENTS_READ, minimumScope: PermissionScope.ALL },
      { permission: PermissionKey.CLIENTS_WRITE, minimumScope: PermissionScope.ALL },
    ],
  },
  /**
   * Edytor materiału (`/products/:id/resources/:resourceId`, także `/add`).
   *
   * Ta sama bramka co przyciski „Dodaj materiał" / „Edytuj" na liście — te stoją
   * na `products.resources.write` (`Product.tsx`, prop `readonly`). Trasa stała
   * wcześniej na `products.manage` ALL, więc head coach z samym
   * `resources.write` widział aktywny przycisk, klikał i wracał na /dashboard.
   * Zapis i tak waliduje RLS — `20260807001600_products_resources_write.sql`
   * przepuszcza `resources.write` ALL oraz ASSIGNED + wiersz w
   * `team_member_product`, więc bramka bez `minimumScope` niczego nie otwiera
   * ponad to, co baza i tak sprawdza.
   */
  productsResourcesWrite: {
    anyOf: [
      { permission: PermissionKey.PRODUCTS_MANAGE, minimumScope: PermissionScope.ALL },
      { permission: PermissionKey.PRODUCTS_RESOURCES_WRITE },
    ],
  },
  programs: { permission: PermissionKey.PROGRAMS_MANAGE },
  /** Sam CRM (dashboard, spotkania, agent, rozliczenia) — bez koszyków. */
  salesCrm: {
    anyOf: [
      { permission: PermissionKey.SALES_READ },
      { permission: PermissionKey.SALES_SETTLEMENTS_READ, minimumScope: PermissionScope.ALL },
    ],
  },
  /**
   * Cała sekcja „Sales CRM" (menu + `/sales/*`). Poza CRM-em mieszczą się w niej
   * koszyki sprzedażowe, więc wpuszcza też kogoś, kto ma wyłącznie
   * `sales.checkout.manage` — inaczej domyślka tej roli (administracja, która na
   * prodzie nie ma ani `sales.read`, ani `sales.settlements.read`) nie dawałaby
   * dostępu do niczego, bo guard rodzica odbijałby na `/dashboard`.
   */
  sales: {
    anyOf: [
      { permission: PermissionKey.SALES_READ },
      { permission: PermissionKey.SALES_SETTLEMENTS_READ, minimumScope: PermissionScope.ALL },
      { permission: PermissionKey.SALES_CHECKOUT_MANAGE, minimumScope: PermissionScope.ALL },
      { permission: PermissionKey.PRODUCTS_MANAGE, minimumScope: PermissionScope.ALL },
    ],
  },
  salesManage: { permission: PermissionKey.SALES_MANAGE },
  // Studio (TRE-1975): ta sama bramka co dashboard marketingowy — właścicielem contentu
  // jest marketing, a RLS na studio_clip stoi na marketing.read/ALL. Start pipeline'u ma
  // dodatkową bramkę agents.use przy przycisku i na serwerze (authorizeStudioAccess).
  studio: { permission: PermissionKey.MARKETING_READ, minimumScope: PermissionScope.ALL },
  /**
   * Zakładka „Koszyki sprzedażowe" (`/sales/checkout-variants`).
   *
   * Lustro polityk na `checkout_variant` i guardu `/generate-checkout-code`:
   * `sales.checkout.manage` ALL (domyślka ownera, administracji i sprzedaży) albo
   * `products.manage` ALL dla osób z nadpisaniem katalogowym. Wcześniej stała tu
   * `sales.manage` w dowolnym zakresie — sprzedawca wchodził na stronę i dostawał
   * 403 z serwisu oraz 406 z PostgREST-a przy każdym zapisie (TRE-2017).
   */
  salesCheckout: {
    anyOf: [
      { permission: PermissionKey.SALES_CHECKOUT_MANAGE, minimumScope: PermissionScope.ALL },
      { permission: PermissionKey.PRODUCTS_MANAGE, minimumScope: PermissionScope.ALL },
    ],
  },
  support: { permission: PermissionKey.SUPPORT_READ },
  surveys: { permission: PermissionKey.SURVEYS_MANAGE },
  team: { permission: PermissionKey.TEAM_READ },
  teamArea: {
    anyOf: [
      { permission: PermissionKey.TEAM_READ },
      { permission: PermissionKey.TEAM_STATS_READ, minimumScope: PermissionScope.ALL },
      { permission: PermissionKey.TEAM_CHAT_METRICS_READ, minimumScope: PermissionScope.ALL },
    ],
  },
  teamStats: { permission: PermissionKey.TEAM_STATS_READ, minimumScope: PermissionScope.ALL },
  traineeList: { permission: PermissionKey.CLIENTS_READ },
} as const satisfies Record<string, PermissionRequirement>;

export type DashboardPermissionArea = keyof typeof dashboardPermissionRequirements;

export const getDashboardPermissionRequirement = (area: string): PermissionRequirement | undefined => {
  if (Object.prototype.hasOwnProperty.call(dashboardPermissionRequirements, area)) {
    return dashboardPermissionRequirements[area as DashboardPermissionArea];
  }

  return undefined;
};
